import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../../api/api.js'; // Use your authenticated API instance
import { API_BASE_URL } from '../../config';
import styles from './JobCreationAssistant.module.css';
import { Mic, X, Send, Bot, Loader2, Sparkles } from 'lucide-react';

const JobCreationAssistant = ({ homeUserId }) => {
    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [voiceStatus, setVoiceStatus] = useState("idle"); // idle, listening, processing, speaking
    const messagesEndRef = useRef(null);

    // Flow State
    const [step, setStep] = useState("init"); 
    // Steps: init -> title -> profession_select -> profession_search -> location -> details -> budget -> timeline -> sitevisit -> summary -> confirm -> finished

    const [suggestedProfessions, setSuggestedProfessions] = useState([]);

    const [jobDraft, setJobDraft] = useState({
        title: "",
        professionId: null,
        professionName: "",
        location: "", 
        state: "", 
        postcode: "", 
        description: "",
        budget: "",
        budgetAmount: null,
        timeline: "",
        siteVisit: ""
    });

    // Mutable ref for async access inside callbacks
    const conversationRef = useRef({ ...jobDraft });

    // --- SCROLL TO BOTTOM ---
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // --- 1. TTS & STT ---
    const speak = async (text, callback) => {
        setVoiceStatus("speaking");
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/AiSearch/speak`, 
                { text: text }, 
                { responseType: 'blob' }
            );
            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);
            audio.play();
            audio.onended = () => {
                setVoiceStatus("idle");
                if (callback) callback();
            };
            audio.onerror = () => {
                setVoiceStatus("idle");
                if (callback) callback();
            };
        } catch (error) {
            console.error("TTS Error", error);
            setVoiceStatus("idle");
            if (callback) callback();
        }
    };

    const listen = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        recognition.lang = 'en-AU';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        setVoiceStatus("listening");
        recognition.start();

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            handleUserResponse(transcript);
        };

        recognition.onerror = () => {
            setVoiceStatus("idle");
        };
    };

    // --- HELPER: ADD MESSAGE ---
    const addBotMessage = (text, options = []) => {
        setMessages(prev => [...prev, { sender: 'bot', text, options }]);
        speak(text);
    };

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { sender: 'user', text }]);
    };

    // --- 2. CONVERSATION FLOW ENGINE ---

    const startConversation = () => {
        setIsOpen(true);
        if (messages.length === 0) {
            setStep("title");
            addBotMessage("Hi! I'm your AI Job Assistant. Briefly describe what you need to get done?");
        }
    };

    // --- CORE LOGIC ---
    const handleUserResponse = async (text) => {
        setVoiceStatus("processing");
        addUserMessage(text);
        
        // --- STEP 1: JOB TITLE CAPTURE ---
        if (step === "title") {
            // Save the raw text as the Job Title (e.g. "Fix my broken fence")
            conversationRef.current.title = text;
            
            // Now try to find the profession
            await searchAndSuggestProfession(text);
        }

        // --- STEP 1b: PROFESSION SELECTION ---
        else if (step === "profession_select") {
            // 1. Check if user clicked/said "None of these"
            if (text.toLowerCase().includes("none") || text.toLowerCase().includes("no")) {
                setStep("profession_search");
                addBotMessage("Okay, no problem. What is the name of the trade or profession you are looking for? (e.g. Plumber, Electrician)");
                setVoiceStatus("idle");
                return;
            }

            // 2. Check if text matches one of the suggestions (Name matching)
            const match = suggestedProfessions.find(p => p.name.toLowerCase() === text.toLowerCase());
            
            if (match) {
                // Exact match from options
                conversationRef.current.professionId = match.id;
                conversationRef.current.professionName = match.name;
                moveToLocationStep();
            } else {
                // User said something else, treat it as a new search term
                await searchAndSuggestProfession(text);
            }
        }

        // --- STEP 1c: MANUAL PROFESSION SEARCH ---
        else if (step === "profession_search") {
            await searchAndSuggestProfession(text);
        }

        // --- STEP 2: LOCATION ---
        else if (step === "location") {
            try {
                const aiRes = await axios.post(`${API_BASE_URL}/api/AiSearch/extract-intent`, {
                    userInput: text,
                    currentService: conversationRef.current.title,
                    currentLocation: null,
                    currentState: null
                });

                const data = aiRes.data;

                if (!data.isComplete && !data.state && !data.location.match(/^\d{4}$/)) {
                     addBotMessage(data.responseToUser);
                } else {
                    const query = data.state ? `${data.location} ${data.state}` : data.location;
                    const locRes = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(query)}`);
                    
                    if (locRes.data && locRes.data.length > 0) {
                        conversationRef.current.postcode = locRes.data[0].postcode;
                        conversationRef.current.location = locRes.data[0].displayName;
                        conversationRef.current.state = locRes.data[0].state;
                        
                        setStep("details");
                        addBotMessage(`Okay, location set to ${locRes.data[0].displayName}. Can you give me more details about the job?`);
                    } else {
                        addBotMessage(`I couldn't verify ${query}. Could you provide the postcode?`);
                    }
                }
            } catch (e) {
                console.error(e);
                addBotMessage("I had trouble with that location. Please type the postcode.");
            }
        }

        // --- STEP 3: DETAILS ---
        else if (step === "details") {
            conversationRef.current.description = text;
            setStep("budget");
            addBotMessage("What is your budget? You can say an amount or select a range.", [
                "$500 - $1000", "$1000 - $2500", "$2500+", "Not Sure"
            ]);
        }

        // --- STEP 4: BUDGET ---
        else if (step === "budget") {
            conversationRef.current.budget = text;
            const numberPattern = /\d+/g;
            const matches = text.match(numberPattern);
            if (matches) conversationRef.current.budgetAmount = parseFloat(matches[0]);

            setStep("timeline");
            addBotMessage("When would you like this done by?", [
                "Urgently", "Within 1 week", "Within 2 weeks", "Flexible"
            ]);
        }

        // --- STEP 5: TIMELINE ---
        else if (step === "timeline") {
            conversationRef.current.timeline = text;
            setStep("sitevisit");
            addBotMessage("Is there a time for site visits?", [
                "By Appointment", "Anytime", "Weekends"
            ]);
        }

        // --- STEP 6: SITE VISIT & SUMMARY ---
        else if (step === "sitevisit") {
            conversationRef.current.siteVisit = text;
            setStep("confirm");
            
            const summary = `
**Summary:**
• Job: ${conversationRef.current.title}
• Professional: ${conversationRef.current.professionName}
• Location: ${conversationRef.current.location}
• Budget: ${conversationRef.current.budget}
            `;
            
            setMessages(prev => [...prev, { sender: 'bot', text: summary }]); 
            addBotMessage("Great! Ready to post? Say 'Yes' to notify professionals, or 'No' to save as draft.", ["Yes, Create Job", "No, Save Draft"]);
        }

        // --- STEP 7: CONFIRM & SUBMIT ---
        else if (step === "confirm") {
            const isYes = text.toLowerCase().includes("yes") || text.toLowerCase().includes("create");
            const isPublish = isYes;

            try {
                const payload = {
                    userId: parseInt(homeUserId || 0),
                    name: conversationRef.current.title,
                    description: conversationRef.current.description,
                    profession: conversationRef.current.professionId 
                        ? [{ ProfessionId: conversationRef.current.professionId, name: conversationRef.current.professionName }] 
                        : [],
                    postcode: conversationRef.current.postcode || "2000",
                    mystate: conversationRef.current.state || "NSW",
                    budget: conversationRef.current.budget,
                    budgetAmount: conversationRef.current.budgetAmount,
                    deadline: conversationRef.current.timeline,
                    siteVisit: conversationRef.current.siteVisit,
                    isPublish: isPublish,
                    isNew: true,
                    userType: 'HomeUser'
                };

                await api.post('/api/Jobs/createjob', payload);

                if (isPublish) {
                    addBotMessage("Job created successfully! Professionals have been notified.");
                } else {
                    addBotMessage("Job saved as a Draft. You can edit it in your dashboard.");
                }
                setStep("finished");

            } catch (err) {
                console.error("Create Job Error", err);
                addBotMessage("Sorry, there was an error creating the job. Please try again later.");
            }
        }
        
        setVoiceStatus("idle");
    };

    // --- HELPER: SEARCH PROFESSION ---
    const searchAndSuggestProfession = async (query) => {
        try {
            const res = await api.get(`/api/Jobs/AISmartSearch?search=${encodeURIComponent(query)}`);
            const matches = res.data || [];

            if (matches.length > 0) {
                setSuggestedProfessions(matches);
                // Create chips from the Top 5 results
                const options = matches.slice(0, 5).map(p => p.name);
                options.push("None of these");

                setStep("profession_select");
                addBotMessage(`I found these professionals for "${query}". Please select the best match:`, options);
            } else {
                // No matches
                setStep("profession_search");
                addBotMessage(`I couldn't find a profession matching "${query}". Could you try a different trade name? (e.g. Carpenter)`);
            }
        } catch (e) {
            setStep("profession_search");
            addBotMessage("I had trouble searching. What is the profession name?");
        }
    };

    const moveToLocationStep = () => {
        setStep("location");
        addBotMessage(`Got it, ${conversationRef.current.professionName}. Where would you like the job to be performed?`);
    };

    // --- UI HELPERS ---
    const handleOptionClick = (optText) => {
        handleUserResponse(optText);
    };

    const handleSendClick = () => {
        if (!inputText.trim()) return;
        handleUserResponse(inputText);
        setInputText("");
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSendClick();
    };

    if (!isOpen) {
        return (
            <button className={styles.fab} onClick={startConversation} title="Voice Assistant">
                <Sparkles className={styles.fabIcon} />
            </button>
        );
    }

    return (
        <div className={styles.chatWindow}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerTitle}>
                    <Bot size={20} /> Job Assistant
                </div>
                <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
                    <X size={20} />
                </button>
            </div>

            {/* Messages */}
            <div className={styles.messageList}>
                {messages.map((msg, i) => (
                    <div key={i} className={`${styles.messageRow} ${msg.sender === 'user' ? styles.userRow : styles.botRow}`}>
                        <div className={`${styles.bubble} ${msg.sender === 'user' ? styles.userBubble : styles.botBubble}`}>
                            <div style={{whiteSpace: 'pre-wrap'}}>{msg.text}</div>
                            {msg.options && (
                                <div className={styles.optionsContainer}>
                                    {msg.options.map(opt => (
                                        <button key={opt} className={styles.optionChip} onClick={() => handleOptionClick(opt)}>
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {voiceStatus === 'processing' && (
                    <div className={styles.messageRow}>
                        <div className={`${styles.bubble} ${styles.botBubble}`}>
                            <Loader2 className={styles.spin} size={16} /> Thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {step !== 'finished' && (
                <div className={styles.inputArea}>
                    <button 
                        className={`${styles.micBtn} ${voiceStatus === 'listening' ? styles.micBtnActive : ''} ${voiceStatus === 'processing' ? styles.micBtnProcessing : ''}`}
                        onClick={listen}
                        disabled={voiceStatus === 'processing' || voiceStatus === 'speaking'}
                    >
                        {voiceStatus === 'processing' ? <Loader2 className={styles.spin} size={20} /> : <Mic size={20} />}
                    </button>
                    <input 
                        className={styles.input} 
                        placeholder="Type or speak..." 
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={voiceStatus !== 'idle'}
                    />
                    <button className={styles.micBtn} onClick={handleSendClick} disabled={!inputText.trim()}>
                        <Send size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default JobCreationAssistant;